import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Video as VideoIcon, Plus, Upload, Play, Camera, Square, Save, Sparkles, Loader2, Brain, Target, Shield, Lightbulb, TrendingUp, AlertCircle, Star, User, Film, Trophy, Scissors, Share2, FileDown } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import usePermissions from '@/hooks/usePermissions';
import { ReadOnlyBanner, NoAccessScreen } from '@/components/PermissionUI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VideosPage({ user, onLogout, permissions }) {
  const { canEdit, canView, isReadOnly } = usePermissions(permissions);
  const [searchParams, setSearchParams] = useSearchParams();
  const [videos, setVideos] = useState([]);
  const [players, setPlayers] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [showHighlightsDialog, setShowHighlightsDialog] = useState(false);
  const [showPlaybackDialog, setShowPlaybackDialog] = useState(false);
  const [selectedVideoForPlayback, setSelectedVideoForPlayback] = useState(null);
  const [selectedVideoForAnalysis, setSelectedVideoForAnalysis] = useState(null);
  const [selectedPlayerForHighlights, setSelectedPlayerForHighlights] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [highlightData, setHighlightData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingHighlights, setIsGeneratingHighlights] = useState(false);
  const [analysisContext, setAnalysisContext] = useState({ opponent: '', notes: '' });
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    video_type: 'game_film',
    team_id: user.team_id
  });
  const [recordingData, setRecordingData] = useState({
    title: '',
    description: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [mediaStream, setMediaStream] = useState(null);
  const mediaRecorderRef = useRef(null);
  const videoPreviewRef = useRef(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {'video/*': []},
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setSelectedFile(acceptedFiles[0]);
      }
    }
  });

  useEffect(() => {
    loadVideos();
    loadPlayers();
    // Check URL params to auto-open upload dialog
    if (searchParams.get('action') === 'upload') {
      setShowUploadDialog(true);
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [mediaStream]);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos?team_id=${user.team_id}`);
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    }
  };

  const loadPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players?team_id=${user.team_id}`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to load players');
    }
  };

  const openHighlights = async (player) => {
    setSelectedPlayerForHighlights(player);
    setShowHighlightsDialog(true);
    setHighlightData(null);
    
    try {
      const response = await axios.get(`${API}/players/${player.id}/highlights`);
      setHighlightData(response.data);
    } catch (error) {
      setHighlightData(null);
    }
  };

  const generateHighlights = async () => {
    if (!selectedPlayerForHighlights) return;
    
    setIsGeneratingHighlights(true);
    try {
      await axios.post(`${API}/players/${selectedPlayerForHighlights.id}/highlights`);
      const response = await axios.get(`${API}/players/${selectedPlayerForHighlights.id}/highlights`);
      setHighlightData(response.data);
      toast.success('Highlight reel generated!');
    } catch (error) {
      toast.error('Failed to generate highlights. Check LLM key balance.');
    } finally {
      setIsGeneratingHighlights(false);
    }
  };

  const createCanvaReel = async () => {
    if (!selectedPlayerForHighlights) return;
    try {
      const response = await axios.post(`${API}/canva/design`, {
        user_id: user.id,
        title: `${selectedPlayerForHighlights.first_name} ${selectedPlayerForHighlights.last_name} Highlight Reel`,
        width: 1080,
        height: 1920
      });
      if (response.data.edit_url) {
        window.open(response.data.edit_url, '_blank');
        toast.success('Reel design created in your Canva account!');
      }
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error('Canva is not configured yet. Connect it from the Social Media page.');
      } else if (error.response?.status === 401) {
        toast.error('Connect your Canva account first from the Social Media page.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create Canva reel');
      }
    }
  };

  const openAnalysis = async (video) => {
    setSelectedVideoForAnalysis(video);
    setShowAnalysisDialog(true);
    setAnalysisData(null);

    // Try to load existing analysis
    try {
      const response = await axios.get(`${API}/videos/${video.id}/analysis`);
      setAnalysisData(response.data);
      if (response.data?.analysis_status === 'generating') {
        pollVideoAnalysis(video.id);
      }
    } catch (error) {
      // No existing analysis, that's ok
      setAnalysisData(null);
    }
  };

  const pollVideoAnalysis = (videoId) => {
    setIsAnalyzing(true);
    const started = Date.now();
    const poll = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/videos/${videoId}/analysis`);
        const status = r.data?.analysis_status;
        if (status && status !== 'generating') {
          clearInterval(poll);
          setIsAnalyzing(false);
          setAnalysisData(r.data);
          if (r.data?.error_message) {
            toast.error('AI analysis failed — check your LLM key balance.');
          } else {
            toast.success('AI analysis complete!');
          }
        }
      } catch (err) {
        // keep polling — analysis row may still be propagating
      }
      if (Date.now() - started > 180000) {
        clearInterval(poll);
        setIsAnalyzing(false);
        toast.error('AI analysis timed out. It may still finish in the background — reopen this dialog in a minute.');
      }
    }, 3000);
  };

  const openPlayback = (video) => {
    setSelectedVideoForPlayback(video);
    setShowPlaybackDialog(true);
  };

  const [highlightVideo, setHighlightVideo] = useState(null);
  const [showHighlightDialog, setShowHighlightDialog] = useState(false);
  const [highlightStart, setHighlightStart] = useState(0);
  const [highlightDuration, setHighlightDuration] = useState(10);
  const [highlightName, setHighlightName] = useState('');
  const [shareToSocial, setShareToSocial] = useState(true);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);
  const highlightPreviewRef = useRef(null);

  const openHighlight = (video) => {
    setHighlightVideo(video);
    setHighlightStart(0);
    setHighlightDuration(10);
    setHighlightName(`${video.title} - Highlight`);
    setShareToSocial(true);
    setShowHighlightDialog(true);
    setVideoDuration(0);
  };

  const submitHighlight = async () => {
    if (!highlightVideo) return;
    setIsCreatingHighlight(true);
    try {
      const res = await axios.post(`${API}/videos/${highlightVideo.id}/highlight`, {
        start_seconds: Number(highlightStart) || 0,
        duration_seconds: Math.max(0.5, Math.min(Number(highlightDuration) || 10, 30)),
        name: highlightName.trim() || 'Highlight',
        share_to_social: shareToSocial,
      });
      if (shareToSocial && res.data?.social_draft_id) {
        toast.success('Highlight created and staged as a social draft!');
      } else {
        toast.success('Highlight created');
      }
      setShowHighlightDialog(false);
      loadVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create highlight');
    } finally {
      setIsCreatingHighlight(false);
    }
  };

  // Download a Player Report Card PDF by matching the entry's jersey number
  // (e.g. "#23 Marcus Johnson") to a player in the roster.
  const downloadPlayerReport = async (entry) => {
    const label = String(entry?.player || '');
    const match = label.match(/#\s*(\d+)/);
    const jersey = match?.[1];
    if (!jersey) {
      toast.error('Could not detect jersey number');
      return;
    }
    const player = players.find((p) => String(p.jersey_number) === jersey);
    if (!player) {
      toast.error(`No roster player with #${jersey}`);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/playbook/player/${player.id}/report.pdf?video_id=${selectedVideoForAnalysis?.id || ''}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${player.last_name || 'player'}_${jersey}_report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Report card downloaded for #${jersey}`);
    } catch (e) {
      toast.error('Failed to generate report card');
    }
  };

  const getVideoUrl = (video) => {
    if (!video?.file_url) return null;
    // If file_url is a relative path, prepend backend URL
    if (video.file_url.startsWith('/')) {
      return `${BACKEND_URL}${video.file_url}`;
    }
    return video.file_url;
  };

  const startAnalysis = async () => {
    if (!selectedVideoForAnalysis) return;

    setIsAnalyzing(true);
    setAnalysisData(null);
    try {
      // Kicks off a background task; response returns almost immediately
      await axios.post(`${API}/videos/${selectedVideoForAnalysis.id}/analyze`, analysisContext);
      toast.info('AI is analyzing the footage… this can take up to a minute.');
      pollVideoAnalysis(selectedVideoForAnalysis.id);
    } catch (error) {
      setIsAnalyzing(false);
      toast.error('Failed to start video analysis. Check LLM key balance.');
    }
  };

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Chunked upload path — used for files > 50 MB to avoid Cloudflare / ingress
  // body-size limits (typical Cloudflare free tier max is ~100 MB).
  const uploadInChunks = async () => {
    const CHUNK_SIZE = 4 * 1024 * 1024;  // 4 MB — safely under CF free tier
    const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);

    // 1) init
    const initForm = new FormData();
    initForm.append('filename', selectedFile.name);
    initForm.append('total_chunks', String(totalChunks));
    initForm.append('total_size', String(selectedFile.size));
    const initRes = await axios.post(`${API}/videos/upload/init`, initForm, { timeout: 60000 });
    const uploadId = initRes.data.upload_id;

    // 2) upload each chunk sequentially
    for (let i = 0; i < totalChunks; i += 1) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
      const blob = selectedFile.slice(start, end);
      const chunkForm = new FormData();
      chunkForm.append('upload_id', uploadId);
      chunkForm.append('chunk_index', String(i));
      chunkForm.append('chunk', blob, `chunk_${i}`);
      await axios.post(`${API}/videos/upload/chunk`, chunkForm, {
        timeout: 5 * 60 * 1000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
    }

    // 3) finalize
    const finalizeForm = new FormData();
    finalizeForm.append('upload_id', uploadId);
    finalizeForm.append('team_id', uploadData.team_id || user.team_id);
    finalizeForm.append('title', uploadData.title);
    finalizeForm.append('description', uploadData.description || '');
    finalizeForm.append('video_type', uploadData.video_type);
    finalizeForm.append('user_id', user.id);
    await axios.post(`${API}/videos/upload/complete`, finalizeForm, { timeout: 5 * 60 * 1000 });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a video file');
      return;
    }
    if (!uploadData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const CHUNK_THRESHOLD = 50 * 1024 * 1024;  // 50 MB
      if (selectedFile.size > CHUNK_THRESHOLD) {
        await uploadInChunks();
      } else {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('title', uploadData.title);
        formData.append('description', uploadData.description || '');
        formData.append('video_type', uploadData.video_type);
        formData.append('team_id', uploadData.team_id || user.team_id);
        formData.append('user_id', user.id);

        await axios.post(`${API}/videos/upload`, formData, {
          timeout: 30 * 60 * 1000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (evt) => {
            if (evt.total) {
              setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
            }
          },
        });
      }

      toast.success('Video uploaded successfully!');
      setShowUploadDialog(false);
      setSelectedFile(null);
      setUploadProgress(0);
      loadVideos();
      setUploadData({
        title: '',
        description: '',
        video_type: 'game_film',
        team_id: user.team_id,
      });
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      const status = error.response?.status;
      if (status === 413) {
        toast.error('File too large. Please upload a smaller video (max 2 GB).');
      } else if (status === 400) {
        toast.error(detail);
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. Try a smaller file or check your connection.');
      } else if (error.message === 'Network Error') {
        toast.error('Network error during upload. This can happen with very large files — the app now uploads in chunks automatically, but check your connection and retry.');
      } else {
        toast.error(`Failed to upload video: ${detail}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const [facingMode, setFacingMode] = useState('user'); // 'user' for front, 'environment' for back

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: facingMode }, 
        audio: true 
      });
      setMediaStream(stream);
      
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        setRecordedChunks(chunks);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started');
    } catch (error) {
      toast.error('Failed to start recording. Please allow camera access.');
    }
  };

  const switchCamera = async () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    
    if (mediaStream && !isRecording) {
      // Stop current stream
      mediaStream.getTracks().forEach(track => track.stop());
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: newFacingMode }, 
          audio: true 
        });
        setMediaStream(stream);
        
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream;
        }
        toast.success(`Switched to ${newFacingMode === 'user' ? 'front' : 'back'} camera`);
      } catch (error) {
        toast.error('Failed to switch camera');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
      
      toast.success('Recording stopped');
    }
  };

  const saveRecording = async () => {
    if (recordedChunks.length === 0) {
      toast.error('No recording to save');
      return;
    }

    const blob = new Blob(recordedChunks, { type: 'video/webm' });
    const formData = new FormData();
    formData.append('file', blob, 'recording.webm');
    formData.append('title', recordingData.title || 'Recorded Video');
    formData.append('description', recordingData.description || 'In-app recording');
    formData.append('video_type', 'recording');
    formData.append('team_id', user.team_id);

    try {
      await axios.post(`${API}/videos/record`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Recording saved successfully!');
      setShowRecordDialog(false);
      setRecordedChunks([]);
      setRecordingData({ title: '', description: '' });
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = null;
      }
      loadVideos();
    } catch (error) {
      toast.error('Failed to save recording');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="videos-page">
        {!canView('videos') ? (
          <NoAccessScreen area="Videos" />
        ) : (
        <>
        {isReadOnly('videos') && <ReadOnlyBanner area="Videos" />}
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Video Library
            </h1>
            <p className="text-muted-foreground mt-2">Upload game film, analyze footage, and create player highlights</p>
          </div>
          {user.role !== 'player' && canEdit('videos') && (
            <div className="flex gap-2">
              <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="record-video-btn">
                    <Camera className="h-4 w-4 mr-2" />
                    RECORD
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">Record Video</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                      <video
                        ref={videoPreviewRef}
                        autoPlay
                        muted
                        className="w-full h-full object-cover"
                        data-testid="video-preview"
                      />
                      {!isRecording && (
                        <Button 
                          onClick={switchCamera}
                          variant="secondary"
                          size="sm"
                          className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
                          data-testid="switch-camera-btn"
                        >
                          🔄 {facingMode === 'user' ? 'Front' : 'Back'}
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2 justify-center">
                      {!isRecording && recordedChunks.length === 0 && (
                        <Button onClick={startRecording} data-testid="start-recording-btn">
                          <Camera className="h-4 w-4 mr-2" />
                          START RECORDING
                        </Button>
                      )}
                      {isRecording && (
                        <Button onClick={stopRecording} variant="destructive" data-testid="stop-recording-btn">
                          <Square className="h-4 w-4 mr-2" />
                          STOP RECORDING
                        </Button>
                      )}
                      {recordedChunks.length > 0 && (
                        <>
                          <Button onClick={() => setRecordedChunks([])} variant="outline">
                            DISCARD
                          </Button>
                          <Button onClick={() => {
                            setRecordedChunks([]);
                            startRecording();
                          }} variant="outline">
                            RECORD AGAIN
                          </Button>
                        </>
                      )}
                    </div>
                    {recordedChunks.length > 0 && (
                      <div className="space-y-4">
                        <div>
                          <Label>Title *</Label>
                          <Input
                            data-testid="recording-title"
                            value={recordingData.title}
                            onChange={(e) => setRecordingData({...recordingData, title: e.target.value})}
                            placeholder="Recording title"
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Input
                            data-testid="recording-description"
                            value={recordingData.description}
                            onChange={(e) => setRecordingData({...recordingData, description: e.target.value})}
                            placeholder="Optional description"
                          />
                        </div>
                        <Button onClick={saveRecording} className="w-full" data-testid="save-recording-btn">
                          <Save className="h-4 w-4 mr-2" />
                          SAVE RECORDING
                        </Button>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="upload-video-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    UPLOAD VIDEO
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">Upload Video</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpload} className="space-y-4" data-testid="upload-video-form">
                    <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/20 hover:border-primary/50'
                    }`} data-testid="video-dropzone">
                      <input {...getInputProps()} />
                      <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      {selectedFile ? (
                        <p className="text-sm font-semibold">{selectedFile.name}</p>
                      ) : (
                        <div>
                          <p className="text-sm font-semibold">Drop video file here or click to browse</p>
                          <p className="text-xs text-muted-foreground mt-1">Supports MP4, MOV, AVI</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        data-testid="video-title"
                        value={uploadData.title}
                        onChange={(e) => setUploadData({...uploadData, title: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        data-testid="video-description"
                        value={uploadData.description}
                        onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Video Type</Label>
                      <Select
                        value={uploadData.video_type}
                        onValueChange={(value) => setUploadData({...uploadData, video_type: value})}
                      >
                        <SelectTrigger data-testid="video-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="game_film">Game Film</SelectItem>
                          <SelectItem value="practice">Practice</SelectItem>
                          <SelectItem value="highlight">Highlight Reel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full" disabled={isUploading} data-testid="submit-video-btn">
                      {isUploading ? `UPLOADING… ${uploadProgress}%` : 'UPLOAD VIDEO'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        <Tabs defaultValue="videos" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="videos" data-testid="videos-tab">
              <VideoIcon className="h-4 w-4 mr-2" />
              Game Film
            </TabsTrigger>
            <TabsTrigger value="highlights" data-testid="highlights-tab">
              <Star className="h-4 w-4 mr-2" />
              Player Highlights
            </TabsTrigger>
          </TabsList>

          <TabsContent value="videos">
            {videos.length === 0 ? (
              <Card className="border-2">
                <CardContent className="p-12 text-center">
                  <VideoIcon className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                  <h3 className="font-heading text-2xl mt-4">No Videos Yet</h3>
                  <p className="text-muted-foreground mt-2">Upload your first game film or record a video</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map((video) => (
                  <Card key={video.id} className="border-2 hover:border-primary/50 transition-all hover:-translate-y-1" data-testid={`video-card-${video.id}`}>
                    <CardHeader className="border-b bg-muted/30">
                      <div className="aspect-video bg-black rounded-md relative overflow-hidden">
                        {video.thumbnail_url ? (
                          <img
                            src={`${BACKEND_URL}${video.thumbnail_url}`}
                            alt={video.title}
                            className="w-full h-full object-cover"
                            data-testid={`video-thumbnail-${video.id}`}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-16 w-16 text-white/50" />
                          </div>
                        )}
                        {video.thumbnail_url && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                            <Play className="h-16 w-16 text-white drop-shadow-lg" />
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <h3 className="font-heading text-xl font-bold uppercase mb-2">{video.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{video.description}</p>
                      <div className="flex items-center justify-between mb-3">
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-bold uppercase rounded-sm">
                          {video.video_type.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(video.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1" 
                          onClick={() => openPlayback(video)}
                          data-testid={`play-video-${video.id}`}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          PLAY
                        </Button>
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => openAnalysis(video)}
                          data-testid={`analyze-video-${video.id}`}
                        >
                          <Sparkles className="h-4 w-4 mr-1" />
                          AI ANALYZE
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          className="flex-1"
                          onClick={() => openHighlight(video)}
                          data-testid={`highlight-video-${video.id}`}
                        >
                          <Scissors className="h-4 w-4 mr-1" />
                          HIGHLIGHT
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="highlights">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <Star className="h-6 w-6 text-yellow-500" />
                  Player Highlight Reels
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-muted-foreground mb-6">
                  Generate AI-powered highlight reels for your players based on analyzed game footage.
                </p>
                {players.length === 0 ? (
                  <div className="text-center py-8">
                    <User className="h-12 w-12 mx-auto text-muted-foreground opacity-50 mb-2" />
                    <p className="text-muted-foreground">No players found. Add players to generate highlights.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {players.map((player) => (
                      <Card 
                        key={player.id} 
                        className="border-2 hover:border-yellow-500/50 cursor-pointer transition-all"
                        onClick={() => openHighlights(player)}
                        data-testid={`player-highlight-card-${player.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-lg font-bold text-primary">
                                #{player.jersey_number || '?'}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold">{player.first_name} {player.last_name}</h4>
                              <p className="text-xs text-muted-foreground">{player.position || 'Player'}</p>
                            </div>
                            <Button variant="ghost" size="sm">
                              <Film className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Player Highlights Dialog */}
        <Dialog open={showHighlightsDialog} onOpenChange={setShowHighlightsDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                <Star className="h-6 w-6 text-yellow-500" />
                {selectedPlayerForHighlights ? `${selectedPlayerForHighlights.first_name} ${selectedPlayerForHighlights.last_name}` : ''} Highlights
              </DialogTitle>
            </DialogHeader>

            {!highlightData && !isGeneratingHighlights && (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <Trophy className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
                <p className="font-semibold text-lg">Generate Highlight Reel</p>
                <p className="text-sm text-muted-foreground mb-4">
                  AI will create a personalized highlight reel based on analyzed game footage
                </p>
                <Button onClick={generateHighlights} data-testid="generate-highlights-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  GENERATE HIGHLIGHTS
                </Button>
              </div>
            )}

            {isGeneratingHighlights && (
              <div className="text-center py-12">
                <Loader2 className="h-16 w-16 animate-spin mx-auto text-yellow-500 mb-4" />
                <p className="font-semibold text-lg">Generating highlight reel...</p>
                <p className="text-sm text-muted-foreground">AI is finding the best moments</p>
              </div>
            )}

            {highlightData && !isGeneratingHighlights && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <Badge className="bg-yellow-100 text-yellow-800">
                    <Star className="h-3 w-3 mr-1" />
                    {highlightData.total_highlights} Highlights
                  </Badge>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={createCanvaReel} data-testid="canva-reel-btn" style={{ borderColor: '#8B3DFF', color: '#8B3DFF' }}>
                      <Film className="h-4 w-4 mr-1" />
                      Create Reel in Canva
                    </Button>
                    <Button variant="outline" size="sm" onClick={generateHighlights}>
                      <Sparkles className="h-4 w-4 mr-1" />
                      Regenerate
                    </Button>
                  </div>
                </div>

                {/* Stats Summary */}
                {highlightData.stats_summary && (
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center p-2 bg-blue-50 rounded">
                      <p className="text-xl font-bold text-blue-600">{highlightData.stats_summary.total_points}</p>
                      <p className="text-xs text-muted-foreground">Points</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                      <p className="text-xl font-bold text-green-600">{highlightData.stats_summary.total_rebounds}</p>
                      <p className="text-xs text-muted-foreground">Rebounds</p>
                    </div>
                    <div className="text-center p-2 bg-purple-50 rounded">
                      <p className="text-xl font-bold text-purple-600">{highlightData.stats_summary.total_assists}</p>
                      <p className="text-xs text-muted-foreground">Assists</p>
                    </div>
                    <div className="text-center p-2 bg-orange-50 rounded">
                      <p className="text-xl font-bold text-orange-600">{highlightData.stats_summary.games_played}</p>
                      <p className="text-xs text-muted-foreground">Games</p>
                    </div>
                  </div>
                )}

                {/* AI Commentary */}
                {highlightData.ai_commentary && (
                  <Card className="border-2 border-yellow-200 bg-yellow-50">
                    <CardContent className="p-4">
                      <p className="text-sm italic">"{highlightData.ai_commentary}"</p>
                    </CardContent>
                  </Card>
                )}

                {/* Highlight List */}
                <div className="space-y-3">
                  <h4 className="font-bold text-lg">Top Highlights</h4>
                  {highlightData.highlights?.map((highlight, idx) => (
                    <Card key={idx} className="border hover:border-primary/50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            highlight.type === 'scoring' ? 'bg-blue-500' :
                            highlight.type === 'defense' ? 'bg-red-500' :
                            highlight.type === 'playmaking' ? 'bg-purple-500' :
                            'bg-green-500'
                          }`}>
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{highlight.description}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {highlight.type}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                ⏱️ {highlight.timestamp}
                              </span>
                              <span className="text-xs">
                                ⭐ {highlight.impact_rating}/10
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* AI Analysis Dialog */}
        <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                <Brain className="h-6 w-6 text-purple-500" />
                AI Video Analysis
              </DialogTitle>
              {selectedVideoForAnalysis && (
                <p className="text-muted-foreground">{selectedVideoForAnalysis.title}</p>
              )}
            </DialogHeader>
            
            {!analysisData && !isAnalyzing && (
              <div className="space-y-4">
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <Sparkles className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
                  <p className="font-semibold text-lg">Ready to analyze this video?</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Claude Vision will pull 8 keyframes from your footage and deliver a technical, per-player breakdown by jersey number — PnR coverage, closeouts, footwork, spacing.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Opponent (optional)</Label>
                    <Input
                      value={analysisContext.opponent}
                      onChange={(e) => setAnalysisContext({ ...analysisContext, opponent: e.target.value })}
                      placeholder="e.g., Eagles, Tigers"
                    />
                  </div>
                  <div>
                    <Label>Game Notes (optional)</Label>
                    <Input
                      value={analysisContext.notes}
                      onChange={(e) => setAnalysisContext({ ...analysisContext, notes: e.target.value })}
                      placeholder="Any context for the AI"
                    />
                  </div>
                </div>
                <Button onClick={startAnalysis} className="w-full" data-testid="start-analysis-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  START AI ANALYSIS
                </Button>
              </div>
            )}

            {isAnalyzing && (
              <div className="text-center py-12">
                <Loader2 className="h-16 w-16 animate-spin mx-auto text-primary mb-4" />
                <p className="font-semibold text-lg">Analyzing video...</p>
                <p className="text-sm text-muted-foreground">AI is reviewing your footage for insights</p>
              </div>
            )}

            {analysisData && !isAnalyzing && (
              <div className="space-y-6">
                {/* Analysis Status */}
                <div className="flex items-center justify-between">
                  <Badge variant={analysisData.analysis_status === 'completed' ? 'default' : 'secondary'}>
                    {analysisData.analysis_status === 'completed' ? '✓ Analysis Complete' : 'Processing...'}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={startAnalysis}>
                    <Sparkles className="h-4 w-4 mr-1" />
                    Re-Analyze
                  </Button>
                </div>

                {analysisData.error_message && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-yellow-800">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-semibold">Note: {analysisData.error_message}</span>
                    </div>
                  </div>
                )}

                {/* AI Summary */}
                {analysisData.ai_summary && (
                  <Card className="border-2 border-primary/30">
                    <CardHeader className="bg-primary/5">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Brain className="h-5 w-5 text-purple-500" />
                        AI Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <p className="text-sm whitespace-pre-wrap">{analysisData.ai_summary}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Per-Player Breakdown */}
                {analysisData.players_detected?.length > 0 && (
                  <Card className="border-2">
                    <CardHeader className="bg-orange-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <User className="h-5 w-5 text-orange-500" />
                        Per-Player Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {analysisData.players_detected.map((entry, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded" data-testid={`per-player-${idx}`}>
                            <Badge className="shrink-0 bg-orange-500 text-white font-bold">
                              {entry.player || `#${idx + 1}`}
                            </Badge>
                            <p className="text-sm whitespace-pre-wrap flex-1">{entry.notes}</p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-orange-500 hover:text-orange-600 shrink-0"
                              onClick={() => downloadPlayerReport(entry)}
                              data-testid={`download-player-report-${idx}`}
                              title="Download report card PDF"
                            >
                              <FileDown className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Offensive Analysis */}
                  {analysisData.offensive_analysis && (
                    <Card className="border-2">
                      <CardHeader className="bg-blue-50">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Target className="h-5 w-5 text-blue-500" />
                          Offensive Analysis
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <p className="text-sm whitespace-pre-wrap">{analysisData.offensive_analysis}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Defensive Analysis */}
                  {analysisData.defensive_analysis && (
                    <Card className="border-2">
                      <CardHeader className="bg-red-50">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Shield className="h-5 w-5 text-red-500" />
                          Defensive Analysis
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <p className="text-sm whitespace-pre-wrap">{analysisData.defensive_analysis}</p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Coaching Insights */}
                {analysisData.coaching_insights?.length > 0 && (
                  <Card className="border-2">
                    <CardHeader className="bg-yellow-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-yellow-500" />
                        Coaching Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <ul className="space-y-2">
                        {analysisData.coaching_insights.map((insight, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-yellow-500 font-bold">{idx + 1}.</span>
                            <span className="text-sm">{insight}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Improvement Areas */}
                {analysisData.improvement_areas?.length > 0 && (
                  <Card className="border-2">
                    <CardHeader className="bg-green-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-green-500" />
                        Areas for Improvement
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <ul className="space-y-2">
                        {analysisData.improvement_areas.map((area, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-green-500">•</span>
                            <span className="text-sm">{area}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Key Plays */}
                {analysisData.plays_identified?.length > 0 && (
                  <Card className="border-2">
                    <CardHeader className="bg-purple-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Play className="h-5 w-5 text-purple-500" />
                        Key Plays Identified
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {analysisData.plays_identified.map((play, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-2 bg-muted/30 rounded">
                            <Badge variant="secondary" className="shrink-0">{idx + 1}</Badge>
                            <p className="text-sm">{play.description}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Video Playback Dialog */}
        <Dialog open={showPlaybackDialog} onOpenChange={setShowPlaybackDialog}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">
                {selectedVideoForPlayback?.title || 'Video Player'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {selectedVideoForPlayback && (
                <>
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                      controls
                      autoPlay
                      className="w-full h-full"
                      src={getVideoUrl(selectedVideoForPlayback)}
                      data-testid="video-player"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-bold uppercase rounded-sm">
                        {selectedVideoForPlayback.video_type?.replace('_', ' ') || 'video'}
                      </span>
                      <span className="ml-3 text-xs text-muted-foreground">
                        {selectedVideoForPlayback.created_at && new Date(selectedVideoForPlayback.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="secondary" 
                        size="sm"
                        onClick={() => {
                          setShowPlaybackDialog(false);
                          openAnalysis(selectedVideoForPlayback);
                        }}
                      >
                        <Sparkles className="h-4 w-4 mr-1" />
                        AI ANALYZE
                      </Button>
                    </div>
                  </div>
                  {selectedVideoForPlayback.description && (
                    <p className="text-sm text-muted-foreground">{selectedVideoForPlayback.description}</p>
                  )}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Highlight Trim Dialog */}
        <Dialog open={showHighlightDialog} onOpenChange={setShowHighlightDialog}>
          <DialogContent className="max-w-2xl" data-testid="highlight-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Scissors className="h-5 w-5" />
                Create Highlight
              </DialogTitle>
            </DialogHeader>
            {highlightVideo && (
              <div className="space-y-4">
                <div className="aspect-video bg-black rounded-md overflow-hidden">
                  <video
                    ref={highlightPreviewRef}
                    src={getVideoUrl(highlightVideo)}
                    controls
                    onLoadedMetadata={(e) => setVideoDuration(e.target.duration || 0)}
                    onTimeUpdate={(e) => {
                      const t = e.target.currentTime;
                      if (t > highlightStart + highlightDuration) {
                        e.target.pause();
                        e.target.currentTime = highlightStart;
                      }
                    }}
                    className="w-full h-full"
                    data-testid="highlight-preview-video"
                  />
                </div>
                <div>
                  <Label>Highlight Name</Label>
                  <Input
                    value={highlightName}
                    onChange={(e) => setHighlightName(e.target.value)}
                    data-testid="highlight-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Start (seconds)</Label>
                    <Input
                      type="number"
                      min={0}
                      step={0.5}
                      max={Math.max(0, (videoDuration || 0) - 1)}
                      value={highlightStart}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        setHighlightStart(v);
                        if (highlightPreviewRef.current) highlightPreviewRef.current.currentTime = v;
                      }}
                      data-testid="highlight-start-input"
                    />
                  </div>
                  <div>
                    <Label>Duration (seconds, max 30)</Label>
                    <Input
                      type="number"
                      min={0.5}
                      max={30}
                      step={0.5}
                      value={highlightDuration}
                      onChange={(e) => setHighlightDuration(Number(e.target.value) || 10)}
                      data-testid="highlight-duration-input"
                    />
                  </div>
                </div>
                {videoDuration > 0 && (
                  <div className="text-xs text-muted-foreground">
                    Source video is {videoDuration.toFixed(1)}s. Highlight will run from {highlightStart.toFixed(1)}s to {(highlightStart + Number(highlightDuration)).toFixed(1)}s.
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareToSocial}
                    onChange={(e) => setShareToSocial(e.target.checked)}
                    data-testid="highlight-share-checkbox"
                  />
                  <Share2 className="h-4 w-4" />
                  Also stage as a Social Media draft
                </label>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setShowHighlightDialog(false)}>Cancel</Button>
                  <Button onClick={submitHighlight} disabled={isCreatingHighlight} data-testid="submit-highlight-btn">
                    {isCreatingHighlight ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Scissors className="h-4 w-4 mr-2" />}
                    {isCreatingHighlight ? 'Trimming...' : 'Create Highlight'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        </>
        )}
      </div>
    </Layout>
  );
}